import React, { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { fmtDate } from "../lib/apiClient";
import TallyInvoicePrint from "../components/TallyInvoicePrint";
import { FilePdf } from "@phosphor-icons/react";
import { toast } from "sonner";

// Public invoice view — no auth, looked up by share token.
// Resolve backend at runtime so production (agrihansa.com) doesn't call the preview backend
// that may have been baked at build time.
function resolveBackend() {
  const envUrl = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
  if (typeof window === "undefined") return envUrl;
  const origin = window.location.origin;
  if (!envUrl) return origin;
  try { return new URL(envUrl).origin === origin ? envUrl : origin; } catch { return origin; }
}
const BACKEND_URL = resolveBackend();

const DOC_LABELS = {
  quotation: "Quotation", sales_order: "Sales Order", purchase_order: "Purchase Order",
  invoice: "Tax Invoice", repair_invoice: "Repair Invoice",
  delivery_challan: "Delivery Challan", gate_pass: "Gate Pass",
  job_work_challan: "Job Work Challan", credit_note: "Credit Note", debit_note: "Debit Note",
};

export default function PublicInvoicePage() {
  const { token } = useParams();
  const [params] = useSearchParams();
  const autoDownload = params.get("dl") === "1";
  const pdfMode = params.get("pdf") === "1";  // server-side Playwright renderer hits us with this flag
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [generating, setGenerating] = useState(false);

  // In pdf=1 mode, hard-remove the Emergent badge from the DOM on mount.
  // CSS `display:none !important` is beaten by the badge's inline `display:inline-flex !important`,
  // so we have to actually delete the element.
  useEffect(() => {
    if (!pdfMode) return;
    const tick = () => {
      const b = document.getElementById("emergent-badge");
      if (b) b.remove();
    };
    tick();
    const t = setTimeout(tick, 200);
    return () => clearTimeout(t);
  }, [pdfMode]);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/public/invoice/${token}`)
      .then(r => setData(r.data))
      .catch(e => setErr(e?.response?.data?.detail || "Invoice unavailable"));
  }, [token]);

  // Per-document <title> so WhatsApp / browser tab show "<Doc Type> <No.> · <Company>"
  useEffect(() => {
    if (!data) return;
    const doc = data.document, company = data.company;
    const lbl = DOC_LABELS[doc.doc_type] || "Document";
    document.title = `${lbl} ${doc.doc_number} · ${company.legal_name || "Ramkishan Agri Innovate"}`;
  }, [data]);

  const downloadPdf = useCallback(async () => {
    if (!data) return;
    const doc = data.document;
    const lbl = DOC_LABELS[doc.doc_type] || "Document";
    const fname = `${lbl.replace(/\s+/g, "_")}_${doc.doc_number}.pdf`;
    setGenerating(true);
    try {
      // Server-rendered PDF — byte-identical to what WhatsApp recipients get via Gallabox
      const res = await fetch(`${BACKEND_URL}/api/public/invoice/${token}/pdf`);
      if (!res.ok) throw new Error(`PDF render failed (${res.status})`);
      const blob = await res.blob();
      const { downloadBlob } = await import("../lib/pdfShare");
      downloadBlob(blob, fname);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error(e.message || "Could not generate PDF");
    } finally { setGenerating(false); }
  }, [data, token]);

  // Auto-trigger PDF download once content is rendered if ?dl=1 was passed (skip in pdf=1 server-render mode)
  useEffect(() => {
    if (!data || !autoDownload || pdfMode) return;
    // Wait one paint so .tally-print-root is in the DOM with images sized
    const t = setTimeout(() => downloadPdf(), 600);
    return () => clearTimeout(t);
  }, [data, autoDownload, pdfMode, downloadPdf]);

  if (err) return (
    <div className="min-h-screen flex items-center justify-center bg-surface p-6" data-testid="public-error">
      <div className="card-base p-8 max-w-md text-center">
        <div className="font-display text-xl font-bold mb-2 text-ink">Link not available</div>
        <div className="text-sm text-ink-muted">{err}. The link may have expired or been revoked by the sender.</div>
      </div>
    </div>
  );
  if (!data) return <div className="min-h-screen flex items-center justify-center text-ink-muted">Loading invoice…</div>;

  const doc = data.document; const company = data.company;
  const party = doc.party_snapshot || {};
  const docLabel = DOC_LABELS[doc.doc_type] || "Document";
  // Build a minimal cfg object so the Tally template knows how to render this doc-type
  const cfg = {
    label: docLabel,
    showTax: ["invoice", "repair_invoice", "quotation", "sales_order", "purchase_order", "credit_note", "debit_note"].includes(doc.doc_type),
    showAdvance: ["invoice", "repair_invoice", "sales_order"].includes(doc.doc_type),
    showTransport: ["invoice", "delivery_challan", "gate_pass", "job_work_challan"].includes(doc.doc_type),
  };

  // pdf=1 mode (used by server-side Playwright renderer): render ONLY the Tally template,
  // no page chrome, no padding, no wrappers. Guarantees the WhatsApp-attached PDF is
  // byte-similar to what the user sees in browser print preview.
  if (pdfMode) {
    return (
      <div data-testid="public-invoice-pdf-mode">
        <style>{`
          /* Hide every UI element except the Tally template — Playwright doesn't
             fire window.print events, so #emergent-badge's JS toggle won't run.
             Force-hide it (and any other floating chrome) here. */
          #emergent-badge, .no-print, [data-emergent-badge] { display: none !important; }
          body { background: #fff !important; margin: 0 !important; }
        `}</style>
        {doc.status === "cancelled" && (
          <div className="cancelled-stamp print-only">CANCELLED</div>
        )}
        <TallyInvoicePrint doc={doc} company={company} party={party} cfg={cfg} docTypeLabel={docLabel} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface" data-testid="public-invoice-page">
      <div className="max-w-[900px] mx-auto p-4 sm:p-6">
        <div className="no-print flex items-center justify-between mb-4 gap-3">
          <div>
            <div className="label-overline text-brand">Shared {docLabel}</div>
            <div className="text-xs text-ink-muted">From {company.legal_name}</div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={downloadPdf}
              disabled={generating}
              className="btn-primary inline-flex items-center gap-1"
              data-testid="public-download-pdf"
            >
              <FilePdf size={14} weight="bold" /> {generating ? "Preparing PDF…" : "Download PDF"}
            </button>
            <button onClick={() => window.print()} className="btn-secondary inline-flex items-center gap-1" data-testid="public-print">
              Print
            </button>
          </div>
        </div>

        {autoDownload && generating && (
          <div className="no-print mb-3 rounded-md border border-[#1C4F32]/40 bg-[#E6F1EA] px-4 py-2.5 text-xs text-[#1C4F32]" data-testid="public-auto-dl-banner">
            Your PDF is downloading automatically — please check your Downloads folder.
          </div>
        )}

        {doc.status === "cancelled" && (
          <div className="no-print mb-3 rounded-md border-2 border-destructive/40 bg-destructive/5 px-4 py-2.5 text-xs">
            <div className="font-display font-semibold text-destructive uppercase tracking-wider">Cancelled</div>
            {doc.cancellation_reason && <div className="text-ink mt-1"><span className="label-overline">Reason:</span> {doc.cancellation_reason}</div>}
            {doc.cancelled_at && <div className="text-ink-muted text-[10px] mt-0.5">Cancelled on {fmtDate(doc.cancelled_at)}</div>}
          </div>
        )}

        <div className="print-container">
          <TallyInvoicePrint doc={doc} company={company} party={party} cfg={cfg} docTypeLabel={docLabel} />
        </div>
        {doc.status === "cancelled" && <div className="cancelled-stamp print-only">CANCELLED</div>}
      </div>
    </div>
  );
}
